import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Typography, Box, Accordion, IconButton,
    AccordionSummary, AccordionDetails, Checkbox, FormControlLabel, Divider
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import usePA_GlobalStore from "../../../Services/ZustandGlobalStore";

// Alignment with your app's existing color scheme
const sectionColorMap = {
    Meals: '#e8f2ed',
    Activities: '#cbe0f7',
    Lodging: '#fef4ec',
    General: '#EDE7F6',
    Flights: '#fcf9dd',
    Supplies: '#FEE5D4',
    Shopping: '#B2D4F0',
    Incidentals: '#FAF3CC',
    Transportation: '#D6F5E4'
};

function ReceiptDialogEditLinkItem({ isOpen, onClose, receipt, onSave }) {

    const { PA_GlobalObject, set_PA_GlobalObject } = usePA_GlobalStore();


    // 1. Internal Data Retrieval (Matches your 'Add New' logic)
    const eventLodging = PA_GlobalObject?.Planning?.lodging || [];
    const eventMeals = PA_GlobalObject?.Planning?.meals || [];
    const eventActivities = PA_GlobalObject?.Planning?.activities || [];
    const eventFlights = PA_GlobalObject?.Planning?.flights || [];

    // Local state to track selections before the user hits "Save"
    const [pendingLink, setPendingLink] = useState({
        section: "General",
        section_item: "",
        amount: 0,
        currency: "USD",
        date: "",
        location: null
    });

    // Sync local state when the dialog is opened with a specific receipt
    useEffect(() => {
        if (isOpen && receipt) {
            setPendingLink({
                section: receipt.section || "General",
                section_item: receipt.section_item || "",
                amount: receipt.amount || 0,
                currency: receipt.currency || "USD",
                date: receipt.date || "",
                location: receipt.location || null
            });
        }
    }, [isOpen, receipt]);

    // Core logic for handling selection and data extraction
    const handleLinkUpdate = (section_id, _section) => {
        // 1. UNSELECT / GENERAL CATEGORY LOGIC
        // If clicking the same item (toggle off) or clicking a category header (-1)
        if (section_id === -1 || (pendingLink.section === _section && pendingLink.section_item === section_id)) {
            setPendingLink(prev => ({
                ...prev,
                section: _section, // Keep the category (e.g., "Meals")
                section_item: "",  // Remove the specific link (e.g., "Meal_123")
                // We do NOT change amount, date, or location here
                // so the user doesn't lose their data.
            }));
            return;
        }

        // 2. SELECT / LINK LOGIC
        const categoryLookup = {
            Lodging: eventLodging.find(i => i.id === section_id),
            Meals: eventMeals.find(i => i.id === section_id),
            Activities: eventActivities.find(i => i.id === section_id),
            Flights: eventFlights.find(i => i.id === section_id),
        };

        const selected = categoryLookup[_section];

        if (selected) {
            // When linking, we DO overwrite with the new parent item's data
            let _amount = selected.amount_total || 0;
            let _currency = selected.currency || "USD";
            let _location = selected.location || null;
            let _date = selected.date || "";

            const matchingAttendee = selected.attendees?.find(att => att.id === receipt.attendee_id);
            if (matchingAttendee) {
                _amount = parseFloat(matchingAttendee.cost || matchingAttendee.amount_total || 0);
            }

            if (selected.time_table?.dates?.[0]) {
                _date = selected.time_table.dates[0].date;
            }

            setPendingLink({
                section: _section,
                section_item: section_id,
                amount: _amount,
                currency: _currency,
                date: _date,
                location: _location
            });
        }
    };

    const handleConfirm = () => {
        // Fires the parent's save function with the newly gathered data
        onSave(receipt.id, {
            section: pendingLink.section,
            section_item: pendingLink.section_item,
            amount: pendingLink.amount,
            currency: pendingLink.currency,
            date: pendingLink.date,
            location: pendingLink.location
        });
        onClose();
    };

    const renderLinkRow = (id, label, subLabel, amount, currency, section) => {
        const isChecked = pendingLink.section === section && pendingLink.section_item === id;

        return (
            <Box
                key={id}
                onClick={() => handleLinkUpdate(id, section)}
                sx={{
                    display: 'flex', alignItems: 'center', p: 1.5, mb: 1, borderRadius: '12px', cursor: 'pointer',
                    border: '1px solid', borderColor: isChecked ? '#151269' : 'transparent',
                    bgcolor: isChecked ? 'rgba(21, 18, 105, 0.04)' : '#fff',
                    transition: 'all 0.2s ease',
                    '&:hover': { bgcolor: isChecked ? 'rgba(21, 18, 105, 0.08)' : '#f5f5f5' }
                }}
            >
                <Checkbox size="small" checked={isChecked} sx={{ color: '#151269', '&.Mui-checked': { color: '#151269' } }} />
                <Box sx={{ flex: 1, minWidth: 0, ml: 1 }}>
                    <Typography variant="body2" noWrap sx={{ fontWeight: isChecked ? 700 : 500, color: '#151269' }}>
                        {label}
                    </Typography>
                    {subLabel && (
                        <Typography variant="caption" color="textSecondary" noWrap display="block">
                            {subLabel}
                        </Typography>
                    )}
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 800, ml: 1, color: '#151269' }}>
                    {new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'USD' }).format(amount)}
                </Typography>
            </Box>
        );
    };

    const CategoryGroup = ({ title, items, section }) => {
        // Logic: Check the main header if the section matches,
        // regardless of whether a specific item or the "general" section is selected.
        const isCategorySelected = pendingLink.section === section;

        return (
            <Accordion
                disableGutters
                elevation={0}
                sx={{
                    '&:before': { display: 'none' },
                    border: '1px solid #eee',
                    borderRadius: '16px !important',
                    mb: 1.5,
                    overflow: 'hidden'
                }}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                        bgcolor: sectionColorMap[section] || '#f8f9fa',
                        minHeight: '56px',
                        '&.Mui-expanded': { minHeight: '56px' }
                    }}
                >
                    <FormControlLabel
                        onClick={(e) => {
                            e.stopPropagation(); // Prevents accordion from closing
                            handleLinkUpdate(-1, section);
                        }}
                        control={
                            <Checkbox
                                size="small"
                                checked={isCategorySelected}
                                sx={{ '&.Mui-checked': { color: '#151269' } }}
                            />
                        }
                        label={<Typography sx={{ fontWeight: 700, fontSize: '14px', color: '#151269' }}>{title}</Typography>}
                    />
                </AccordionSummary>
                <AccordionDetails sx={{ p: 1.5, bgcolor: '#fafafa', maxHeight: '300px', overflowY: 'auto' }}>
                    {items.length > 0 ? items.map(item => {
                        let label = item.location?.name || item.title || item.reservation_name || "Untitled";
                        let subLabel = "";

                        if (section === "Flights") {
                            label = `${item.airline_code || ''} ${item.flight_number || ''}`.trim();
                            subLabel = `${item.depart_airport_code || '?'} → ${item.arrive_airport_code || '?'}`;
                        } else if (section === "Lodging") {
                            subLabel = item.room_details || "";
                        } else if (section === "Meals") {
                            subLabel = item.eat_in_details || "";
                        }

                        const cost = item.attendees?.find(a => a.id === receipt.attendee_id)?.cost ?? item.amount_total ?? 0;

                        // Passing e.stopPropagation inside the row click handler
                        return (
                            <Box
                                key={item.id}
                                onClick={(e) => {
                                    e.stopPropagation(); // Double insurance to keep accordion open
                                    handleLinkUpdate(item.id, section);
                                }}
                            >
                                {renderLinkRow(item.id, label, subLabel, cost, item.currency, section)}
                            </Box>
                        );
                    }) : (
                        <Typography variant="caption" sx={{ p: 2, display: 'block', textAlign: 'center', color: '#999' }}>
                            No items found.
                        </Typography>
                    )}
                </AccordionDetails>
            </Accordion>
        );
    };

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            PaperProps={{ sx: { borderRadius: '25px', width: '100%', maxWidth: 500, padding: '4px' } }}
        >
            <DialogTitle sx={{
                background: 'linear-gradient(135deg, #151269 0%, #352fcc 100%)',
                color: 'white',
                p: 3,
                textAlign: 'center',
                borderRadius: '20px 20px 10px 10px',
                position: 'relative'
            }}>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>LINK TO EVENT</Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>Sync information from planned items</Typography>
                <IconButton onClick={onClose} sx={{ position: 'absolute', right: 12, top: 12, color: 'white' }}>
                    <i className="icon-cross" style={{ fontSize: '18px' }} />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ mt: 2, pt: 1, px: 2 }}>
                <CategoryGroup title="Lodging" items={eventLodging} section="Lodging" />
                <CategoryGroup title="Meals" items={eventMeals} section="Meals" />
                <CategoryGroup title="Activities" items={eventActivities} section="Activities" />
                <CategoryGroup title="Flights" items={eventFlights} section="Flights" />

                <Divider sx={{ my: 3 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#bbb' }}>GENERAL CATEGORIES</Typography>
                </Divider>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center', pb: 2 }}>
                    {['Supplies', 'Incidentals', 'Shopping', 'Transportation', 'General'].map(cat => (
                        <Button
                            key={cat}
                            variant={pendingLink.section === cat ? "contained" : "outlined"}
                            onClick={() => handleLinkUpdate(-1, cat)}
                            sx={{
                                borderRadius: '20px',
                                textTransform: 'none',
                                px: 2,
                                fontWeight: 600,
                                bgcolor: pendingLink.section === cat ? '#151269' : 'transparent',
                                borderColor: '#151269',
                                color: pendingLink.section === cat ? 'white' : '#151269',
                                '&:hover': { bgcolor: pendingLink.section === cat ? '#352fcc' : 'rgba(21, 18, 105, 0.05)', borderColor: '#151269' }
                            }}
                        >
                            {cat}
                        </Button>
                    ))}
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 3 }}>
                <Button
                    onClick={handleConfirm}
                    fullWidth
                    variant="contained"
                    sx={{ bgcolor: '#151269', borderRadius: '15px', py: 1.5, fontWeight: 700, '&:hover': { bgcolor: '#352fcc' } }}
                >
                    Finish & Sync
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default ReceiptDialogEditLinkItem;